import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight, Archive, ArchiveRestore, Download, ClipboardCopy, Globe, FileText, RefreshCw, Bot, Package, Trash2, Pause, X } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { NewAnalysisModal } from "@/components/NewAnalysisModal";
import { BatchImportModal } from "@/components/BatchImportModal";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { tasksApi, type Task, type KeywordStats, projectsApi } from "@/api";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { SyncLabelFilter, SyncLabelBadges } from "@/components/SyncLabelFilter";
import { syncLabelsApi, type SyncLabel } from "@/api";

// Tag color mapping
const tagColorClasses: Record<string, string> = {
  blue: "border-blue-500 text-blue-400",
  green: "border-green-500 text-green-400",
  red: "border-red-500 text-red-400",
  yellow: "border-yellow-500 text-yellow-400",
  purple: "border-purple-500 text-purple-400",
  orange: "border-orange-500 text-orange-400",
  default: "border-muted-foreground text-muted-foreground",
};

// Categorize task source — single source of truth for filter + display
function getTaskSource(t: Pick<Task, 'url' | 'success_strategy' | 'serp_request_id'>): string {
  if (t.success_strategy?.startsWith("browseract")) return "browseract";
  if (t.url?.includes("alibaba.com/product-detail")) return "alibaba-product";
  if (t.url?.includes("alibaba.com")) return "alibaba-search";
  if (t.serp_request_id) return "serp";
  return "manual";
}

const sourceLabels: Record<string, string> = {
  "manual": "手動網址",
  "serp": "SERP 自動",
  "browseract": "BrowserAct",
  "alibaba-search": "阿里搜尋",
  "alibaba-product": "阿里產品",
};

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "待處理", variant: "outline" },
  processing: { label: "處理中", variant: "secondary" },
  completed: { label: "已完成", variant: "default" },
  failed: { label: "失敗", variant: "destructive" },
};

export default function Tasks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const pageSize = 20;
  const queryClient = useQueryClient();
  const [showKeywordPanel, setShowKeywordPanel] = useState(false);

  // Multi-select filter states (not in URL for simplicity)
  const [sourceMultiFilter, setSourceMultiFilter] = useState<Set<string>>(new Set());
  const [projectFilter, setProjectFilter] = useState<Set<string>>(new Set());
  const [keywordFilter, setKeywordFilter] = useState<Set<string>>(new Set());
  const [productFilter, setProductFilter] = useState<Set<string>>(new Set());

  // Sync label filters (from APP14/APP16 classification)
  const [slBuyerStage, setSlBuyerStage] = useState<Set<string>>(new Set());
  const [slSerpType, setSlSerpType] = useState<Set<string>>(new Set());
  const [slGroup, setSlGroup] = useState<Set<string>>(new Set());
  const [slTag, setSlTag] = useState<Set<string>>(new Set());

  // Read state from URL params
  const statusFilter = searchParams.get("status") || "all";
  const searchQuery = searchParams.get("q") || "";
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const showArchived = searchParams.get("archived") === "1";

  // Helpers to update URL params
  const updateParam = useCallback((key: string, value: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === null || value === "" || (key === "status" && value === "all") || (key === "page" && value === "1") || (key === "archived" && value === "0")) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setStatusFilter = (v: string) => { updateParam("status", v); updateParam("page", null); };
  const setSearchQuery = (v: string) => { updateParam("q", v); updateParam("page", null); };
  const setCurrentPage = (v: number | ((p: number) => number)) => {
    const newPage = typeof v === "function" ? v(currentPage) : v;
    updateParam("page", String(newPage));
  };
  const toggleArchived = () => {
    updateParam("archived", showArchived ? null : "1");
    updateParam("page", null);
    setSelectedTasks(new Set());
  };

  const { data: allTasks = [], isLoading, error } = useQuery<Task[]>({
    queryKey: ["tasks", showArchived ? "archived" : "active"],
    queryFn: async () => {
      const res = await tasksApi.list({
        limit: 5000,
        archived: showArchived,
        status: statusFilter !== "all" ? statusFilter : undefined,
      });
      return res.data || [];
    },
    refetchInterval: 10000,
  });

  // Fetch project names for ID→name mapping
  const { data: projectNameMap } = useQuery({
    queryKey: ["projectNames"],
    queryFn: async () => {
      const res = await projectsApi.list();
      const map = new Map<string, string>();
      for (const p of res.data || []) {
        map.set(p.project_id, p.name);
      }
      return map;
    },
    staleTime: 60_000,
  });

  // Fetch sync labels for keyword matching
  const { data: syncLabelsData } = useQuery({
    queryKey: ["syncLabels"],
    queryFn: async () => {
      const res = await syncLabelsApi.getAll();
      const map = new Map<string, SyncLabel>();
      for (const sl of res.labels || []) {
        map.set(sl.keyword_norm, sl);
      }
      return map;
    },
    staleTime: 60_000,
  });

  // Build dynamic filter options from loaded tasks
  const filterOptions = useMemo(() => {
    const sources = new Map<string, number>();
    const projects = new Map<string, number>();
    const keywords = new Map<string, number>();
    const products = new Map<string, number>();
    for (const t of allTasks) {
      // Sources
      const src = getTaskSource(t);
      sources.set(src, (sources.get(src) || 0) + 1);
      // Projects
      const pid = t.project_id || "(無專案)";
      projects.set(pid, (projects.get(pid) || 0) + 1);
      // Keywords
      if (t.target_keyword) {
        keywords.set(t.target_keyword, (keywords.get(t.target_keyword) || 0) + 1);
      }
      // Tags (from backend)
      if (t.tags) {
        for (const tag of t.tags) {
          products.set(tag.tag_name, (products.get(tag.tag_name) || 0) + 1);
        }
      }
    }
    const toOptions = (m: Map<string, number>, labelMap?: Record<string, string>) =>
      [...m.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([value, count]) => ({ value, label: labelMap?.[value] || value, count }));
    // Build project label map from projectNameMap
    const projectLabels: Record<string, string> = {};
    if (projectNameMap) {
      for (const [pid, name] of projectNameMap.entries()) {
        projectLabels[pid] = name;
      }
    }
    return {
      sources: toOptions(sources, sourceLabels),
      projects: toOptions(projects, projectLabels),
      keywords: toOptions(keywords),
      products: toOptions(products),
    };
  }, [allTasks, projectNameMap]);

  const filtered = allTasks.filter(t => {
    const ref = t.url || t.target_keyword || t.task_id || "";
    if (searchQuery && !ref.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    // Multi-select filters
    if (sourceMultiFilter.size > 0) {
      if (!sourceMultiFilter.has(getTaskSource(t))) return false;
    }
    if (projectFilter.size > 0) {
      const pid = t.project_id || "(無專案)";
      if (!projectFilter.has(pid)) return false;
    }
    if (keywordFilter.size > 0) {
      if (!t.target_keyword || !keywordFilter.has(t.target_keyword)) return false;
    }
    if (productFilter.size > 0) {
      const taskTagNames = t.tags?.map(tag => tag.tag_name) || [];
      if (!taskTagNames.some(name => productFilter.has(name))) return false;
    }
    // Sync label filters
    if (slBuyerStage.size > 0 || slSerpType.size > 0 || slGroup.size > 0 || slTag.size > 0) {
      const sl = t.target_keyword ? syncLabelsData?.get(t.target_keyword) : undefined;
      if (!sl) return false;
      const labels = sl.labels || {};
      if (slBuyerStage.size > 0 && !slBuyerStage.has(String(labels.buyer_stage || ''))) return false;
      if (slSerpType.size > 0 && !slSerpType.has(String(labels.serp_type || ''))) return false;
      if (slGroup.size > 0 && !slGroup.has(sl.group_path || '')) return false;
      if (slTag.size > 0) {
        const slTags = Array.isArray(sl.tags) ? sl.tags : [];
        if (!slTags.some((tag: string) => slTag.has(tag))) return false;
      }
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Keyword management panel
  const { data: keywordStats = [], refetch: refetchKeywordStats } = useQuery<KeywordStats[]>({
    queryKey: ["keywordStats"],
    queryFn: async () => {
      const res = await tasksApi.keywordStats();
      return res.data || [];
    },
    enabled: showKeywordPanel,
    refetchInterval: showKeywordPanel ? 10000 : false,
  });

  const deleteByKeywordMutation = useMutation({
    mutationFn: (keyword: string) => tasksApi.deleteByKeyword(keyword),
    onSuccess: (_, keyword) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      refetchKeywordStats();
      toast.success(`已刪除「${keyword}」的所有待處理任務`);
    },
    onError: (err: Error) => toast.error(`刪除失敗: ${err.message}`),
  });

  const pauseByKeywordMutation = useMutation({
    mutationFn: (keyword: string) => tasksApi.pauseByKeyword(keyword),
    onSuccess: (_, keyword) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      refetchKeywordStats();
      toast.success(`已暫停「${keyword}」的所有待處理任務`);
    },
    onError: (err: Error) => toast.error(`暫停失敗: ${err.message}`),
  });

  const archiveMutation = useMutation({
    mutationFn: (taskIds: string[]) => tasksApi.batchUpdate(taskIds, "archive"),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["tasks"] }); toast.success("任務已封存"); setSelectedTasks(new Set()); },
    onError: (err: Error) => toast.error(`封存失敗: ${err.message}`),
  });

  const unarchiveMutation = useMutation({
    mutationFn: (taskIds: string[]) => tasksApi.batchUpdate(taskIds, "unarchive"),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["tasks"] }); toast.success("任務已還原"); setSelectedTasks(new Set()); },
    onError: (err: Error) => toast.error(`還原失敗: ${err.message}`),
  });

  const browseractRetryMutation = useMutation({
    mutationFn: ({ taskId, language }: { taskId: string; language?: string }) =>
      tasksApi.browseractRetry(taskId, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("BrowserAct 重試已啟動");
    },
    onError: (err: Error) => toast.error(`BrowserAct 重試失敗: ${err.message}`),
  });

  // Detect TW domain for pre-selecting language
  const isTwDomain = (url: string) => {
    const lower = url.toLowerCase();
    return ['.tw', 'shopee', 'ruten', 'pchome', 'momo'].some(d => lower.includes(d));
  };

  const handleToggleSelect = (taskId: string) => {
    const s = new Set(selectedTasks);
    s.has(taskId) ? s.delete(taskId) : s.add(taskId);
    setSelectedTasks(s);
  };

  const handleSelectAll = () => {
    setSelectedTasks(selectedTasks.size === paged.length ? new Set() : new Set(paged.map(t => t.task_id)));
  };

  // Copy filtered URLs to clipboard
  const handleCopyUrls = () => {
    const targetTasks = selectedTasks.size > 0
      ? filtered.filter(t => selectedTasks.has(t.task_id))
      : filtered;
    const urls = targetTasks.map(t => t.url).join('\n');
    navigator.clipboard.writeText(urls);
    toast.success(`已複製 ${targetTasks.length} 個 URL`);
  };

  // Export filtered tasks as CSV
  const handleExportCsv = () => {
    const targetTasks = selectedTasks.size > 0
      ? filtered.filter(t => selectedTasks.has(t.task_id))
      : filtered;
    const header = 'task_id,url,domain,status,keyword,priority,created_at';
    const rows = targetTasks.map(t =>
      [t.task_id, `"${t.url}"`, t.domain, t.status, t.target_keyword || '', t.priority || '', t.created_at].join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks_${statusFilter !== 'all' ? statusFilter + '_' : ''}${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`已匯出 ${targetTasks.length} 筆任務`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">任務列表</h1>
          <p className="text-muted-foreground">管理爬蟲分析任務</p>
        </div>
        <div className="flex items-center gap-2">
          <BatchImportModal type="tasks" />
          <NewAnalysisModal />
        </div>
      </div>

      {/* Keyword Management Panel */}
      <Card>
        <CardHeader className="py-3 cursor-pointer" onClick={() => setShowKeywordPanel(!showKeywordPanel)}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              {showKeywordPanel ? "▼" : "▶"} 關鍵字任務管理 Keyword Task Manager
            </h3>
            <span className="text-xs text-muted-foreground">
              {showKeywordPanel ? "點擊收合" : "展開查看各關鍵字任務統計與批次操作"}
            </span>
          </div>
        </CardHeader>
        {showKeywordPanel && (
          <CardContent className="pt-0">
            {keywordStats.length === 0 ? (
              <p className="text-sm text-muted-foreground">載入中...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>關鍵字 Keyword</TableHead>
                    <TableHead className="text-center">總計</TableHead>
                    <TableHead className="text-center">待處理</TableHead>
                    <TableHead className="text-center">處理中</TableHead>
                    <TableHead className="text-center">完成</TableHead>
                    <TableHead className="text-center">失敗</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {keywordStats.map(kw => (
                    <TableRow key={kw.keyword}>
                      <TableCell className="font-medium">{kw.keyword}</TableCell>
                      <TableCell className="text-center">{kw.total}</TableCell>
                      <TableCell className="text-center">
                        {kw.pending > 0 ? <Badge variant="outline">{kw.pending}</Badge> : "0"}
                      </TableCell>
                      <TableCell className="text-center">
                        {kw.processing > 0 ? <Badge variant="secondary">{kw.processing}</Badge> : "0"}
                      </TableCell>
                      <TableCell className="text-center">
                        {kw.completed > 0 ? <Badge>{kw.completed}</Badge> : "0"}
                      </TableCell>
                      <TableCell className="text-center">
                        {kw.failed > 0 ? <Badge variant="destructive">{kw.failed}</Badge> : "0"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {kw.pending > 0 && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  if (confirm(`確定暫停「${kw.keyword}」的 ${kw.pending} 個待處理任務？`)) {
                                    pauseByKeywordMutation.mutate(kw.keyword);
                                  }
                                }}
                                disabled={pauseByKeywordMutation.isPending}
                              >
                                <Pause className="h-3 w-3 mr-1" /> 暫停
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  if (confirm(`確定刪除「${kw.keyword}」的 ${kw.pending} 個待處理任務？此操作無法復原！`)) {
                                    deleteByKeywordMutation.mutate(kw.keyword);
                                  }
                                }}
                                disabled={deleteByKeywordMutation.isPending}
                              >
                                <Trash2 className="h-3 w-3 mr-1" /> 刪除待處理
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Checkbox checked={selectedTasks.size === paged.length && paged.length > 0} onCheckedChange={handleSelectAll} disabled={paged.length === 0} />
              <span className="text-sm text-muted-foreground">{selectedTasks.size > 0 ? `已選擇 ${selectedTasks.size} 個任務` : "全選"}</span>
            </div>
            <div className="flex items-center gap-2">
              {selectedTasks.size > 0 && (
                showArchived ? (
                  <Button variant="outline" size="sm" onClick={() => unarchiveMutation.mutate(Array.from(selectedTasks))} disabled={unarchiveMutation.isPending}>
                    <ArchiveRestore className="h-4 w-4 mr-2" /> 還原
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => archiveMutation.mutate(Array.from(selectedTasks))} disabled={archiveMutation.isPending}>
                    <Archive className="h-4 w-4 mr-2" /> 封存
                  </Button>
                )
              )}
              <Button variant={showArchived ? "default" : "outline"} size="sm" onClick={toggleArchived}>
                <Archive className="h-4 w-4 mr-2" /> {showArchived ? "顯示活動任務" : "顯示封存任務"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopyUrls} title={selectedTasks.size > 0 ? `複製 ${selectedTasks.size} 個選取的 URL` : `複製全部 ${filtered.length} 個 URL`}>
                <ClipboardCopy className="h-4 w-4 mr-2" /> 複製 URL
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCsv} title={selectedTasks.size > 0 ? `匯出 ${selectedTasks.size} 個選取的任務` : `匯出全部 ${filtered.length} 筆任務`}>
                <Download className="h-4 w-4 mr-2" /> 匯出 CSV
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="搜尋 URL 或關鍵字..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
            </div>
            <MultiSelectFilter label="來源 Source" options={filterOptions.sources} selected={sourceMultiFilter} onChange={(s) => { setSourceMultiFilter(s); updateParam("page", null); }} />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部狀態</SelectItem>
                <SelectItem value="pending">待處理</SelectItem>
                <SelectItem value="processing">處理中</SelectItem>
                <SelectItem value="completed">已完成</SelectItem>
                <SelectItem value="failed">失敗</SelectItem>
              </SelectContent>
            </Select>
            {filterOptions.projects.length > 1 && (
              <MultiSelectFilter label="專案 Project" options={filterOptions.projects} selected={projectFilter} onChange={(s) => { setProjectFilter(s); updateParam("page", null); }} />
            )}
            {filterOptions.keywords.length > 1 && (
              <MultiSelectFilter label="關鍵字 Keyword" options={filterOptions.keywords} selected={keywordFilter} onChange={(s) => { setKeywordFilter(s); updateParam("page", null); }} />
            )}
            {filterOptions.products.length > 0 && (
              <MultiSelectFilter label="標籤 Tag" options={filterOptions.products} selected={productFilter} onChange={(s) => { setProductFilter(s); updateParam("page", null); }} />
            )}
          </div>
          <SyncLabelFilter
            buyerStage={slBuyerStage} onBuyerStageChange={(s) => { setSlBuyerStage(s); updateParam("page", null); }}
            serpType={slSerpType} onSerpTypeChange={(s) => { setSlSerpType(s); updateParam("page", null); }}
            group={slGroup} onGroupChange={(s) => { setSlGroup(s); updateParam("page", null); }}
            tag={slTag} onTagChange={(s) => { setSlTag(s); updateParam("page", null); }}
          />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center text-muted-foreground py-8">載入中...</div>
          ) : error ? (
            <div className="text-center text-destructive py-8">載入失敗</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"><Checkbox checked={selectedTasks.size === paged.length && paged.length > 0} onCheckedChange={handleSelectAll} disabled={paged.length === 0} /></TableHead>
                    <TableHead>任務 ID</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>來源</TableHead>
                    <TableHead>狀態</TableHead>
                    <TableHead>關鍵字</TableHead>
                    <TableHead>Tags 標籤</TableHead>
                    <TableHead>建立時間</TableHead>
                    <TableHead className="w-24">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">{showArchived ? "無封存任務" : "無任務"}</TableCell></TableRow>
                  ) : paged.map(task => (
                    <TableRow key={task.task_id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="w-12">
                        <Checkbox checked={selectedTasks.has(task.task_id)} onCheckedChange={() => handleToggleSelect(task.task_id)} onClick={(e) => e.stopPropagation()} />
                      </TableCell>
                      <TableCell>
                        <Link to={`/tasks/${task.task_id}`} className="text-primary hover:underline font-mono text-sm">{task.task_id.substring(0, 8)}...</Link>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{task.url}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {task.success_strategy?.startsWith("browseract") ? (
                            <Badge variant="outline" className="text-xs gap-1 border-purple-500 text-purple-400"><Bot className="h-3 w-3" />BrowserAct</Badge>
                          ) : task.url?.includes("alibaba.com/product-detail") ? (
                            <Badge variant="outline" className="text-xs gap-1 border-orange-500 text-orange-400"><Package className="h-3 w-3" />阿里產品</Badge>
                          ) : task.url?.includes("alibaba.com") ? (
                            <Badge variant="outline" className="text-xs gap-1 border-orange-500 text-orange-400"><Globe className="h-3 w-3" />阿里搜尋</Badge>
                          ) : task.serp_request_id ? (
                            <Badge variant="outline" className="text-xs gap-1"><Globe className="h-3 w-3" />SERP</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs gap-1"><FileText className="h-3 w-3" />手動</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell><Badge variant={statusConfig[task.status]?.variant}>{statusConfig[task.status]?.label || task.status}</Badge></TableCell>
                      <TableCell className="text-sm">
                        <div>{task.target_keyword || "—"}</div>
                        {task.target_keyword && syncLabelsData?.get(task.target_keyword) && (
                          <SyncLabelBadges
                            labels={syncLabelsData.get(task.target_keyword)!.labels}
                            groupPath={syncLabelsData.get(task.target_keyword)!.group_path}
                            tags={syncLabelsData.get(task.target_keyword)!.tags as string[]}
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px]">
                        {task.tags && task.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {task.tags.map(tag => (
                              <Badge key={tag.id} variant="outline" className={`text-xs ${tagColorClasses[tag.color] || tagColorClasses.default}`}>
                                {tag.tag_name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{new Date(task.created_at).toLocaleString("zh-TW")}</TableCell>
                      <TableCell>
                        {task.status === "failed" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-purple-400 hover:text-purple-300" onClick={(e) => e.stopPropagation()} disabled={browseractRetryMutation.isPending}>
                                <RefreshCw className="h-3 w-3 mr-1" />
                                <Bot className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem onClick={() => browseractRetryMutation.mutate({ taskId: task.task_id, language: isTwDomain(task.url) ? 'zh_TW' : 'en_US' })}>
                                <RefreshCw className="h-3 w-3 mr-2" />
                                BrowserAct 重試 ({isTwDomain(task.url) ? 'TW' : 'US'})
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => browseractRetryMutation.mutate({ taskId: task.task_id, language: 'zh_TW' })}>
                                🇹🇼 TW Workflow
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => browseractRetryMutation.mutate({ taskId: task.task_id, language: 'en_US' })}>
                                🇺🇸 US/EN Workflow
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">共 {filtered.length} 筆，第 {currentPage} / {totalPages} 頁</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                  <Button variant="outline" size="icon" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
